/**
 * The Jarvis core — an audio-reactive HUD rendered on `<canvas>` (ADR-0025).
 *
 * A self-contained `requestAnimationFrame` loop draws an arc-reactor core, a
 * circular equalizer ring, and rotating tech arcs. The ring + core scale with a
 * live **level** (0..1): during `listening` that's the real mic amplitude
 * (`levelRef`, pumped by the parent from `startMicAnalyser`); during
 * `speaking`/`thinking`/`idle` it's a synthetic oscillation so the orb always
 * feels alive. Drawing happens entirely off React state (ref-driven) to stay at
 * 60fps without re-rendering.
 */
import * as React from "react";

export type OrbPhase = "idle" | "listening" | "thinking" | "speaking";

export function VoiceOrb({
  phase,
  levelRef,
  size = 300,
}: {
  phase: OrbPhase;
  levelRef: React.MutableRefObject<number>;
  size?: number;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const phaseRef = React.useRef(phase);
  phaseRef.current = phase;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const baseR = size * 0.27;
    let raf = 0;
    let t = 0;
    let shown = 0; // eased display level

    const arcRing = (radius: number, rot: number, segs: number, gap: number, width: number, alpha: number) => {
      ctx.lineWidth = width;
      ctx.strokeStyle = `rgba(34,211,238,${alpha})`;
      const seg = (Math.PI * 2) / segs;
      for (let i = 0; i < segs; i++) {
        const a0 = rot + i * seg;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, a0, a0 + seg - gap);
        ctx.stroke();
      }
    };

    const draw = () => {
      t += 1;
      const ph = phaseRef.current;
      let target: number;
      if (ph === "listening") target = levelRef.current;
      else if (ph === "speaking")
        target = 0.32 + 0.34 * Math.abs(Math.sin(t * 0.13)) + 0.16 * Math.abs(Math.sin(t * 0.29 + 1));
      else if (ph === "thinking") target = 0.14 + 0.07 * (0.5 + 0.5 * Math.sin(t * 0.09));
      else target = 0.07 + 0.04 * (0.5 + 0.5 * Math.sin(t * 0.045)); // idle breathing
      shown += (target - shown) * 0.22;
      const lvl = Math.max(0, Math.min(1, shown));

      ctx.clearRect(0, 0, size, size);
      ctx.globalCompositeOperation = "lighter"; // additive glow

      // Ambient core glow
      const glowR = baseR * (0.9 + lvl * 0.9);
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      grd.addColorStop(0, `rgba(165,243,252,${0.45 + lvl * 0.4})`);
      grd.addColorStop(0.45, `rgba(34,211,238,${0.18 + lvl * 0.28})`);
      grd.addColorStop(1, "rgba(34,211,238,0)");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
      ctx.fill();

      // Circular equalizer — reacts to level, per-bar pseudo-spectrum
      const bars = 100;
      const ringR = baseR * 1.18;
      for (let i = 0; i < bars; i++) {
        const ang = (i / bars) * Math.PI * 2 + t * 0.004;
        const n = 0.55 + 0.45 * Math.sin(i * 0.7 + t * 0.16) * Math.sin(i * 0.27 - t * 0.08);
        const amp = lvl * n * baseR * 0.7;
        const r2 = ringR + 3 + amp;
        ctx.strokeStyle = `rgba(34,211,238,${0.28 + n * 0.5})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(ang) * ringR, cy + Math.sin(ang) * ringR);
        ctx.lineTo(cx + Math.cos(ang) * r2, cy + Math.sin(ang) * r2);
        ctx.stroke();
      }

      // Rotating tech arcs + tick ring
      arcRing(baseR * 1.6, t * 0.010, 3, 0.55, 2, 0.5);
      arcRing(baseR * 1.82, -t * 0.007, 5, 0.28, 1.5, 0.32);
      arcRing(baseR * 2.02, t * 0.004, 36, 0.1, 1, 0.22);

      // Core disc
      const coreR = baseR * (0.5 + lvl * 0.16);
      const cg = ctx.createRadialGradient(cx - coreR * 0.3, cy - coreR * 0.3, 0, cx, cy, coreR);
      cg.addColorStop(0, "rgba(224,251,255,0.95)");
      cg.addColorStop(0.5, "rgba(34,211,238,0.85)");
      cg.addColorStop(1, "rgba(37,99,235,0.7)");
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = `rgba(224,251,255,${0.4 + lvl * 0.4})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR + 6, 0, Math.PI * 2);
      ctx.stroke();

      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [size, levelRef]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: "block" }} aria-hidden />;
}
